import { ReactNode, useEffect, useState } from "react"

import type { Dimension, DimensionOption } from "models/ratecard"
import { css, cx } from "@emotion/css"
//eslint-disable-next-line
import { queryCache, useMutation, useQuery } from "react-query"

import { BREAKDOWN_SEMANTIC_CODE } from "./utils"
import Button from "ui/buttons/Button"
import Checkbox from "@material-ui/core/Checkbox"
import { DurationConfigModal } from "./DurationConfigModal"
import { ReactComponent as EditIcon } from "assets/icon-edit.svg"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import IconButton from "ui/buttons/IconButton"
import type { MediaFieldSemantic } from "routes/Base/Admin/GlobalMediaTypes/types"
import { fetchMediaFieldsSemantics } from "api/globalMediaTypes"
import { v1 as getUuid } from "uuid"
import { updateBaseRateConfig } from "api/ratecard"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useToggle } from "hooks/useToggle"

type DimensionItem = {
  id: string
  code: string
  label: string
  enabled: boolean
  options?: DimensionOption[]
}

type Items = Record<string, DimensionItem>

const baseRateSemantics = new URLSearchParams({
  isBaseRateDimension: "true",
}).toString()

function getInitialItems(semantics: MediaFieldSemantic[]): Items {
  const xs = semantics.map((x) => ({
    id: x.code,
    label: x.name,
    enabled: true,
    code: x.code,
  }))
  return Object.fromEntries(xs.map((x) => [x.id, x]))
}

type BaseRateDimensionsProps = {
  baseRateId: string
  baseRateParams: string
}

export function BaseRateDimensions(props: BaseRateDimensionsProps) {
  const { baseRateId, baseRateParams } = props
  const { t } = useI18n()
  const fdb = useSendFeedBack()

  const { data: semantics, updatedAt: semanticsChanged } = useQuery(
    ["media-fields/semantics", baseRateSemantics],
    fetchMediaFieldsSemantics,
  )

  //
  const getInitialState = (): Items => getInitialItems(semantics ?? [])
  const [items, setItems] = useState<Items>(getInitialState)
  useEffect(() => setItems(getInitialState), [semanticsChanged]) //eslint-disable-line

  const handleDimensionEnable = (id: string, enabled: boolean) => {
    setItems((s) => {
      const r = s[id]
      r.enabled = enabled
      return { ...s, [id]: r }
    })
  }

  //#region Duration
  const durationModal = useToggle()
  const setDurations = (options: DimensionOption[]) => {
    setItems((s) => {
      const r = s[BREAKDOWN_SEMANTIC_CODE]
      r.options = options
      return { ...s, [BREAKDOWN_SEMANTIC_CODE]: r }
    })
    durationModal.onClose()
  }
  const getDurations = () =>
    items[BREAKDOWN_SEMANTIC_CODE]?.options?.map((o) => ({
      ...o,
      id: getUuid(),
    })) || []

  //#endregion

  const [createConfig, result] = useMutation(updateBaseRateConfig, {
    onSuccess: () => {
      queryCache.invalidateQueries(["base-rate", baseRateParams])
    },
  })

  const hasDuration =
    items.duration?.enabled && !!items.duration.options?.length

  const handleSubmit = () => {
    const dimensions: Dimension[] = Object.values(items).map((x) => {
      return {
        mediaFieldSemanticCode: x.id,
        enabled: x.enabled,
        options: x.options ?? [],
      }
    })

    const atLeastOneEnabled = dimensions.some((s) => Boolean(s.enabled))
    const breakDownEnabledHasOptions =
      !items[BREAKDOWN_SEMANTIC_CODE].enabled ||
      (items[BREAKDOWN_SEMANTIC_CODE].options?.length ?? 0) > 0

    const valid = atLeastOneEnabled && breakDownEnabledHasOptions
    if (!valid) {
      fdb(t("base.rate.config.invalid"), "error")
      return
    }

    createConfig({ baseRateId, dimensions })
  }

  return (
    <div className={styles.container}>
      <div className={cx(styles.center, styles.pb)}>
        <p className={styles.title}>{t("rate.card.dimensions.title")}</p>
        <p className={styles.subtitle}>{t("rate.card.dimensions.subtitle")}</p>
      </div>
      <div
        className={cx(
          styles.pb,
          css`
            display: flex;
            justify-content: center;
          `,
        )}
      >
        <div>
          <div
            className={css`
              display: flex;
              flex-direction: column;
              padding: 8px 0;
            `}
          >
            {Object.values(items)?.map((d) => {
              const label =
                d?.code === "targetB"
                  ? t("rate.card.dimensions.targetB")
                  : d?.label
              return (
                <Item
                  key={d.id}
                  id={d.id}
                  label={label}
                  enabled={d.enabled}
                  onChange={handleDimensionEnable}
                >
                  {d.id === BREAKDOWN_SEMANTIC_CODE ? (
                    <IconButton
                      aria-label="configure duration"
                      onClick={durationModal.onOpen}
                    >
                      <EditIcon />
                    </IconButton>
                  ) : null}
                </Item>
              )
            })}
          </div>
        </div>
      </div>

      <div className={cx(styles.center)}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={result.isLoading || !hasDuration}
        >
          {t("actions.done")}
        </Button>
      </div>

      <DurationConfigModal
        key={`${durationModal.open}`} // remount
        open={durationModal.open}
        onClose={durationModal.onClose}
        onDone={setDurations}
        getInitial={getDurations}
      />
    </div>
  )
}

type ItemProps = {
  label: string
  id: string
  onChange: (id: string, enabled: boolean) => void
  enabled: boolean
  children?: ReactNode
}

function Item(props: ItemProps) {
  const { label, id, onChange, enabled, children = null } = props

  const handleChange = () => onChange(id, !enabled)
  return (
    <div
      className={css`
        display: flex;
        gap: 8px;
      `}
    >
      <FormControlLabel
        label={label}
        className={cx({ [styles.disabledCheckbox]: id === "duration" })}
        control={
          <Checkbox
            color="primary"
            checked={enabled}
            onChange={handleChange}
            className={styles.checkbox}
            disabled={id === "duration"}
          />
        }
      />
      {children}
    </div>
  )
}

const styles = {
  container: css`
    padding: var(--spacing-xxl-5) var(--spacing-m);
  `,
  pb: css`
    padding-bottom: var(--spacing-xl);
  `,
  center: css`
    text-align: center;
  `,
  title: css`
    font-size: 13px;
    line-height: 16px;
    font-weight: bold;
  `,
  subtitle: css`
    padding-bottom: var(--spacing-xs);
    font-size: 13px;
    line-height: 16px;
    font-weight: 300;
    color: var(--dark-grey);
  `,
  checkbox: css`
    padding: 2px 9px;
  `,
  disabledCheckbox: css`
    .MuiFormControlLabel-label.Mui-disabled {
      color: var(--black);
    }
  `,
}
